import { useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { getDaycareRevenue } from '../../../feature/dashboard/DashboardSlice'

const periods = ['6M', '1Y', 'All']

function BarChartWidget({ month, year }) {
    const dispatch = useDispatch()
    const { revenueData } = useSelector((state) => state.dashboards)
    const [active, setActive] = useState('1Y')

    useEffect(() => { dispatch(getDaycareRevenue({ month, year, range: active })) }, [month, year, active])

    const chart = revenueData?.chart || {}
    const summary = revenueData?.summary || {}

    const labels = chart.labels || []
    const thisYear = chart.thisYear || []
    const lastYear = chart.lastYear || []
    const hasData = labels.length > 0

    const { mrr = 0, projected = 0, growthMoM = 0, growthYoY = 0, currency = 'USD' } = summary
    const symbol = currency === 'USD' ? '$' : currency

    const fmtGrowth = (val) => {
        const n = Number(val) || 0
        return `${n >= 0 ? '↑' : '↓'} ${Math.abs(n)}%`
    }

    const option = {
        tooltip: {
            trigger: 'axis',
            formatter: (p) => p.map((s) => `${s.seriesName}: ${symbol}${(s.value || 0).toLocaleString()}`).join('<br/>')
        },
        legend: { show: false },
        grid: { left: 60, right: 20, top: 20, bottom: 40 },
        xAxis: {
            type: 'category', data: labels,
            axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#aaa' }
        },
        yAxis: {
            type: 'value',
            axisLabel: { formatter: (v) => `${symbol}${v >= 1000 ? v / 1000 + 'K' : v}`, color: '#aaa' },
            splitLine: { lineStyle: { color: '#f0f0f0' } }, axisLine: { show: false }, axisTick: { show: false }
        },
        series: [
            {
                name: 'This year', type: 'line', smooth: true, data: thisYear,
                symbol: 'circle', symbolSize: 6,
                lineStyle: { color: '#6366f1', width: 2 },
                itemStyle: { color: '#6366f1' },
                areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(99,102,241,0.15)' }, { offset: 1, color: 'rgba(99,102,241,0)' }] } }
            },
            {
                name: 'Last year', type: 'line', smooth: true, data: lastYear,
                symbol: 'circle', symbolSize: 5,
                lineStyle: { color: '#22c55e', width: 2, type: 'dashed' },
                itemStyle: { color: '#22c55e' },
            },
        ],
    }

    const momColor = growthMoM >= 0 ? '#22c55e' : '#ef4444'
    const yoyColor = growthYoY >= 0 ? '#22c55e' : '#ef4444'

    return (
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>Revenue Overview</div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Monthly recurring revenue · {revenueData?.range || active}</div>
                </div>
                <div style={{ display: 'flex', gap: 4, background: '#f5f5f5', borderRadius: 8, padding: 3 }}>
                    {periods.map((p) => (
                        <button key={p} onClick={() => setActive(p)}
                            style={{ border: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, background: active === p ? '#6366f1' : 'transparent', color: active === p ? '#fff' : '#888', transition: 'all 0.2s' }}>
                            {p}
                        </button>
                    ))}
                </div>
            </div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}>
                    <span style={{ display: 'inline-block', width: 24, height: 2, background: '#6366f1', borderRadius: 2 }} /> This year
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}>
                    <span style={{ display: 'inline-block', width: 24, height: 2, background: '#22c55e', borderRadius: 2 }} /> Last year
                </div>
            </div>
            <div style={{ position: 'relative' }}>
                <ReactECharts option={option} style={{height:"40vh" }} />
                {!hasData && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>No revenue data for this period</div>
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', gap: 32, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f5f5f5' }}>
                {[
                    { label: 'MRR', value: `${symbol}${mrr.toLocaleString()}` },
                    { label: 'Projected', value: `${symbol}${projected.toLocaleString()}` },
                    { label: 'Growth (MoM)', value: fmtGrowth(growthMoM), color: momColor },
                    { label: 'Growth (YoY)', value: fmtGrowth(growthYoY), color: yoyColor },
                ].map((s) => (
                    <div key={s.label}>
                        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: s.color || '#111' }}>{s.value}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default BarChartWidget
